import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import { getSiteConfig } from "@/config/siteConfigs";
import styles from "./Form.module.scss";

const ContactForm: React.FC = () => {
  const { t } = useTranslation("common");
  const siteConfig = getSiteConfig();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const target = e.target as HTMLInputElement;
      setFormData((prevData) => ({
        ...prevData,
        [name]: target.checked,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.consent) {
      alert(t("contactForm.errors.consent"));
      return;
    }
    if (!captchaToken) {
      alert(t("contactForm.errors.recaptcha"));
      return;
    }

    const formPayload = {
      type: "feedback",
      feedbacktype: "feedbackform",
      legid: "1",
      language: siteConfig.language,
      data: {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: formData.message,
        recaptchaToken: captchaToken,
      },
    };

    try {
      setLoading(true);
      await sendToCRM(formPayload);

      if (window.dataLayer) {
        window.dataLayer.push({
          event: "form_submit",
          form_name: "contact_form",
          form_type: "feedbackform",
        });
      }

      alert(t("contactForm.success"));

      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        consent: false,
      });
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (error) {
      alert(t("contactForm.error"));
    } finally {
      setLoading(false);
    }
  };

  const sendToCRM = async (data: any) => {
    const res = await fetch("https://crm.hixa.eu/external/goodsapi/", {
      method: "POST",
      headers: {
        Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        type: data.type,
        legid: "1",
        language: siteConfig.language,
        feedbacktype: data.feedbacktype,
        data: JSON.stringify(data.data),
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed to send message to CRM: ${res.status} ${text}`);
    }
    return res.json();
  };

  return (
    <div className={`container top12 ${styles.contactSection}`}>
      <div className={`sideP88 sectionContainerWhite ${styles.formContainer}`}>
        <div className={styles.contactSectionBlock}>
          <div className={styles.sectionDesk}>
            <h3 className={styles.heading}>{t("contactForm.heading")}</h3>
            <p className={styles.subheading}>{t("contactForm.subheading")}</p>
          </div>
          <form className={styles.feedbackForm} onSubmit={handleSubmit}>
            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.label}>
                  {t("contactForm.fields.name.label")} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder={`${t("contactForm.fields.name.placeholder")} *`}
                  value={formData.name}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  {t("contactForm.fields.email.label")} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={`${t("contactForm.fields.email.placeholder")} *`}
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phone" className={styles.label}>
                {t("contactForm.fields.phone.label")}
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                placeholder={t("contactForm.fields.phone.placeholder")}
                value={formData.phone}
                onChange={handleChange}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.label}>
                {t("contactForm.fields.message.label")} *
              </label>
              <textarea
                id="message"
                name="message"
                placeholder={`${t("contactForm.fields.message.placeholder")} *`}
                value={formData.message}
                onChange={handleChange}
                className={styles.textarea}
                required
              />
            </div>

            <div className={styles.consent}>
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              <label htmlFor="consent">
                <span className="textBasic">{t("contactForm.consent")} </span>
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  {t("contactForm.consentLink")}
                </a>
                .
              </label>
            </div>

            <div className={styles.formGroup}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={siteConfig.recaptcha.siteKey}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              type="submit"
              className={`commonButton buttonMd ${styles.submitButton}`}
              disabled={loading || !formData.consent || !captchaToken}
            >
              <span>
                {loading
                  ? t("contactForm.submitting")
                  : t("contactForm.submit")}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
